import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { LeadStage } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { LeadFormFields } from "@/components/LeadFormFields";
import type { CatalogFieldsValues } from "@/components/CatalogFields";
import { useUpdateLead } from "@/hooks/useLeads";
import { useLeadDetail } from "@/hooks/useLeadDetail";
import { useCatalogContext } from "@/hooks/useCatalog";
import { useCrmStore } from "@/stores/crm.store";
import { usePipelineStagesQuery } from "@/hooks/usePipelineStages";

type FormValues = {
  name: string;
  phone: string;
  email: string;
};

type FormErrors = {
  name?: string;
  phone?: string;
  email?: string;
};

function validate(values: FormValues): FormErrors {
  const errors: FormErrors = {};
  const phone = values.phone.trim();
  if (phone && phone.length < 8)
    errors.phone = "El teléfono debe tener al menos 8 caracteres";
  if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
    errors.email = "El correo no tiene un formato válido";
  return errors;
}

export function EditLeadModal(): React.JSX.Element {
  const isOpen = useCrmStore((s) => s.isEditModalOpen);
  const leadId = useCrmStore((s) => s.editingLeadId);
  const close = useCrmStore((s) => s.closeEditModal);
  const updateLead = useUpdateLead();
  const { data: catalogContext } = useCatalogContext();
  const { data: stages } = usePipelineStagesQuery();

  const { data: lead } = useLeadDetail(leadId ?? "");

  const [values, setValues] = useState<FormValues>({
    name: "",
    phone: "",
    email: "",
  });
  const [stage, setStage] = useState<LeadStage>("new");
  const [catalogValues, setCatalogValues] = useState<CatalogFieldsValues>({
    cycleId: undefined,
    productId: undefined,
    capturedFields: {},
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Pre-populate when lead data arrives
  useEffect(() => {
    if (lead) {
      setValues({
        name: lead.name ?? "",
        phone: lead.phone ?? "",
        email: lead.email ?? "",
      });
      setStage(lead.stage);
      setCatalogValues({
        cycleId: lead.cycleId ?? undefined,
        productId: lead.productId ?? undefined,
        capturedFields: (lead.capturedFields as Record<string, string> | null) ?? {},
      });
    }
  }, [lead]);

  function handleChange(field: keyof FormValues, value: string): void {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleOpenChange(open: boolean): void {
    if (!open) {
      close();
      setErrors({});
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!leadId) return;
    const errs = validate(values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    await updateLead.mutateAsync(
      {
        leadId,
        name: values.name.trim() || undefined,
        phone: values.phone.trim() || undefined,
        email: values.email.trim() || undefined,
        stage,
        ...(catalogValues.cycleId ? { cycleId: catalogValues.cycleId } : {}),
        ...(Object.keys(catalogValues.capturedFields).length > 0
          ? { capturedFields: catalogValues.capturedFields }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success("Lead actualizado");
          handleOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.message ?? "Error al actualizar el lead");
        },
      },
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Editar lead</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-1">
          <form id="edit-lead-form" onSubmit={(e) => void handleSubmit(e)}>
            <LeadFormFields
              values={values}
              errors={errors}
              onChange={handleChange}
              disabled={updateLead.isPending}
              catalogContext={catalogContext}
              catalogValues={catalogValues}
              onCatalogChange={setCatalogValues}
              showProduct={false}
            />

            <div className="mt-4 flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-700">Etapa</label>
              <Select
                value={stage}
                onValueChange={(v) => setStage(v as LeadStage)}
                disabled={updateLead.isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(stages ?? []).map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </form>
        </div>

        <DialogFooter className="mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={updateLead.isPending}
          >
            Cancelar
          </Button>
          <Button type="submit" form="edit-lead-form" disabled={updateLead.isPending}>
            {updateLead.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
